// Third Party
import React from 'react';

// Proposal
import * as ProposalNS from '../../../Proposal';
import { runBuildRules, validateDisableRules } from '../../../ProposalHelpers';

// Utils
import User from '@/types/user';
import LocalizedString from '@/types/localized-string';

// Components
import DivisionTitle from './DivisionTitle';
import ProposalSection from './ProposalSection';

type ProposalDivisionProps = {
  division: ProposalNS.ProposalTemplateDivisionElement;
  proposal: ProposalNS.Proposal;
  user: User;
  selectedSections: string[];
  selectedDivisions: string[];
  filterQuery: string;
  results: LocalizedString[];
  overrideTooltipPosition?: (coords: { left: number; top: number }) => { left: number; top: number };
  onToggleSection: (section: string, selected: boolean) => void;
  onToggleDivision: (division: string, selected: boolean) => void;
  onToggleHiddenLayout: (layout: string, selected: boolean) => void;
  hiddenLayouts: string[];
  selectedSectionsLocalState: Map<string, boolean>;
  setSelectedSectionsLocalState: React.Dispatch<React.SetStateAction<Map<string, boolean>>>;
  showLoaders: boolean;
};

const ProposalDivision = ({
  division,
  proposal,
  user,
  selectedSections,
  selectedDivisions,
  filterQuery,
  results,
  overrideTooltipPosition,
  onToggleSection,
  onToggleDivision,
  onToggleHiddenLayout,
  hiddenLayouts,
  selectedSectionsLocalState,
  setSelectedSectionsLocalState,
  showLoaders,
}: ProposalDivisionProps) => {
  const { buildRules } = division;

  if (buildRules !== undefined && !runBuildRules(buildRules, proposal, user)) {
    return null;
  }

  const { isDisabled, sidebarTooltip } =
    division.disableRules == null
      ? { isDisabled: false, sidebarTooltip: undefined }
      : validateDisableRules(division.disableRules, selectedDivisions, proposal.items, proposal.riskProfile);

  if (!division.sections.some(section => section.sidebarControl !== ProposalNS.ProposalSidebarControl.Hide)) {
    return null;
  }

  let showWholeDivision = true;

  if (filterQuery.length > 2 && !results.includes(division.label)) {
    showWholeDivision = false;
  }

  return (
    <section className={`options-division ${division.showDivider && filterQuery.length < 2 ? 'show-divider' : ''}`} key={division.key}>
      {(division.showTitle === undefined || division.showTitle === true) && showWholeDivision && (
        <DivisionTitle
          division={division}
          selectedDivisions={selectedDivisions}
          disabled={isDisabled}
          sidebarTooltip={sidebarTooltip}
          toggleDivision={onToggleDivision}
          overrideTooltipPosition={overrideTooltipPosition}
        />
      )}
      {division.sections.map((section, index) => (
        <ProposalSection
          key={`${section.key}-${index}`}
          section={section}
          showWholeDivision={showWholeDivision}
          divisionDisabled={isDisabled}
          proposal={proposal}
          user={user}
          selectedSections={selectedSections}
          filterQuery={filterQuery}
          overrideTooltipPosition={overrideTooltipPosition}
          results={results}
          onToggleSection={onToggleSection}
          selectedSectionsLocalState={selectedSectionsLocalState}
          setSelectedSectionsLocalState={setSelectedSectionsLocalState}
          showLoaders={showLoaders}
          onToggleHiddenLayout={onToggleHiddenLayout}
          hiddenLayouts={hiddenLayouts}
        />
      ))}
    </section>
  );
};

export default ProposalDivision;
